export type AssistantChartType =
  | 'line'
  | 'bar'
  | 'pie'
  | 'scatter'
  | 'radar'
  | 'funnel'
  | 'heatmap'
  | 'gauge'
  | 'sparkline'

export type AssistantRangePreset = 'last_90' | 'this_month' | 'last_month' | 'this_year' | 'custom'

export type AssistantToolPrefs = {
  /** When true, the assistant may call renderChart. */
  graphing: boolean
  /** Chart types the user wants available. Empty while graphing is off. */
  chartTypes: AssistantChartType[]
  /** Documents read per collection. 800, 2,000, or 5,000. */
  maxRecords: number
  rangePreset: AssistantRangePreset
  rangeStart: string
  rangeEnd: string
}

export const ALL_CHART_TYPES: AssistantChartType[] = [
  'line',
  'bar',
  'pie',
  'scatter',
  'radar',
  'funnel',
  'heatmap',
  'gauge',
  'sparkline',
]

export const SCAN_RECORD_CHOICES = [800, 2000, 5000] as const

export const RANGE_PRESET_LABELS: Record<AssistantRangePreset, string> = {
  last_90: 'Last 90 days',
  this_month: 'This month',
  last_month: 'Last month',
  this_year: 'This year',
  custom: 'Custom',
}

export const CHART_TYPE_LABELS: Record<AssistantChartType, string> = {
  line: 'Line',
  bar: 'Bar',
  pie: 'Pie',
  scatter: 'Scatter',
  radar: 'Radar',
  funnel: 'Funnel',
  heatmap: 'Heatmap',
  gauge: 'Gauge',
  sparkline: 'Sparkline',
}

/** Chip text after “Visualize this in a”. */
export function chartOptionLabel(type: AssistantChartType): string {
  const name = CHART_TYPE_LABELS[type].toLowerCase()
  if (type === 'heatmap' || type === 'gauge' || type === 'sparkline') return name
  return `${name} chart`
}

const STORAGE_KEY = 'otw.assistant.toolPrefs'
const DEBUG_KEY = 'otw.assistant.debugMode'

const DEFAULT_PREFS: AssistantToolPrefs = {
  graphing: false,
  chartTypes: [],
  maxRecords: 800,
  rangePreset: 'last_90',
  rangeStart: '',
  rangeEnd: '',
}

export function normalizeChartTypes(value: unknown): AssistantChartType[] {
  if (!Array.isArray(value)) return []
  const seen = new Set(value.filter((item): item is AssistantChartType => ALL_CHART_TYPES.includes(item as AssistantChartType)))
  return ALL_CHART_TYPES.filter(type => seen.has(type))
}

function normalizeMaxRecords(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)
  if (n === 2000 || n === 5000) return n
  return 800
}

function normalizeRangePreset(value: unknown): AssistantRangePreset {
  if (
    value === 'last_90' ||
    value === 'this_month' ||
    value === 'last_month' ||
    value === 'this_year' ||
    value === 'custom'
  ) {
    return value
  }
  return 'last_90'
}

function normalizeDate(value: unknown): string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : ''
}

export function normalizeToolPrefs(value: Partial<AssistantToolPrefs> | null | undefined): AssistantToolPrefs {
  return {
    graphing: Boolean(value?.graphing) && normalizeChartTypes(value?.chartTypes).length > 0,
    chartTypes: value?.graphing ? normalizeChartTypes(value?.chartTypes) : [],
    maxRecords: normalizeMaxRecords(value?.maxRecords),
    rangePreset: normalizeRangePreset(value?.rangePreset),
    rangeStart: normalizeDate(value?.rangeStart),
    rangeEnd: normalizeDate(value?.rangeEnd),
  }
}

export function readToolPrefs(): AssistantToolPrefs {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return normalizeToolPrefs(DEFAULT_PREFS)
    return normalizeToolPrefs(JSON.parse(raw) as Partial<AssistantToolPrefs>)
  } catch {
    return normalizeToolPrefs(DEFAULT_PREFS)
  }
}

export function writeToolPrefs(prefs: AssistantToolPrefs): void {
  const next = normalizeToolPrefs(prefs)
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // ignore
  }
}

/** Debug notes under answers. On until the user turns them off. */
export function readDebugMode(): boolean {
  try {
    return sessionStorage.getItem(DEBUG_KEY) !== '0'
  } catch {
    return true
  }
}

export function writeDebugMode(enabled: boolean): void {
  try {
    sessionStorage.setItem(DEBUG_KEY, enabled ? '1' : '0')
  } catch {
    // ignore
  }
}

export function toggleChartType(
  prefs: AssistantToolPrefs,
  type: AssistantChartType,
  enabled: boolean,
): AssistantToolPrefs {
  const set = new Set(prefs.chartTypes)
  if (enabled) set.add(type)
  else set.delete(type)
  const chartTypes = ALL_CHART_TYPES.filter(t => set.has(t))
  return {
    ...prefs,
    graphing: chartTypes.length > 0,
    chartTypes,
  }
}
